import { useEffect, useSyncExternalStore } from 'react';

/**
 * Unsaved work, and the question a reload or a close of the tab asks about it.
 *
 * Four editors hold a draft that is saved only when its author says so: the report designer,
 * the POI type designer, the app settings page and the report branding page. Leaving one by
 * a link inside the application unmounts it, and the router cannot ask first, so the draft is
 * kept here and is there again on the way back. Here is the tab's memory: a reload or a
 * close of the tab loses what is kept, wherever in the application its author is by then,
 * and cuts off a save or an upload that is still on its way. So one listener, which outlives
 * every editor and decides when the event comes, has the browser ask first.
 *
 * Kept drafts are their author's alone. Signing out does not reload the tab: they stay for
 * the same person signing in again, as after a session that ran out in the middle of the
 * work, nobody is asked about them meanwhile, and they go when somebody else signs in.
 *
 * The store is not read in here: who is signed in is said by the session's code
 * (`followSession`), so every rule can be read, and run, by itself.
 */

/** The editors that keep drafts, by the name each keeps its own under. */
export type DraftEditor = 'report-design' | 'poi-type' | 'app-settings' | 'report-branding';

interface Kept<T> {
  draft: T;
  /** False for a draft its editor keeps although it found nothing in it to save. */
  unsaved: boolean;
  /** When it was last kept. Only a list read after that says anything of the draft (`dropDraftsNotListed`). */
  keptAt: number;
}

/**
 * One editor's kept drafts, by what each is a draft of. Every call names the user it is made
 * for, and a draft is read only by the user it was kept for.
 */
export interface KeptDrafts<T> {
  /** The draft this user left of something, if any. */
  read: (userId: string | undefined, key: string) => T | undefined;
  /** Every draft this user left, by what it is of. */
  readAll: (userId: string | undefined) => Record<string, T>;
  /**
   * Keeps a draft for the user at the editor, who is the one signed in: whatever anybody
   * else left goes. `unsaved` is false for a draft that holds nothing to save as far as its
   * editor can tell: it is there on the way back, but is neither asked about nor marked in a
   * list. An editor that cannot tell yet leaves it out, and the draft stays what it was kept
   * as before, unsaved if it is new: not knowing never turns a draft into one that is not
   * asked about.
   */
  keep: (userId: string | undefined, key: string, draft: T, unsaved?: boolean) => void;
  /** Drops what the user at the screen has kept of something: saved, discarded, or gone with what it was a draft of. */
  drop: (userId: string | undefined, key: string) => void;
  /**
   * What a write that ended after its editor was left makes of a draft: the draft as that
   * write leaves it, or `undefined` once nothing of it is unsaved. Changes nothing unless the
   * kept drafts are still that user's: somebody else may have signed in while the write was
   * on its way, and `keep` would take their drafts from them.
   */
  leave: (userId: string | undefined, key: string, draft: T | undefined, unsaved?: boolean) => void;
}

const shelves = new Map<DraftEditor, Map<string, Kept<unknown>>>();

/** Whose the kept drafts are: one user's at a time, in all four editors. */
let owner: string | undefined;

/** Who is signed in, as the session's code last said. */
let signedIn: string | undefined;

/** The editors on screen that hold something unsettled. */
const onScreen = new Set<symbol>();

/** The writes on their way, each with who was signed in when it was queued. */
const underway = new Map<Promise<unknown>, string>();

// What is kept is counted so that a list on screen can follow it: a write that ends after
// its editor was left adds or drops a draft with nobody there to tell the list.
const watchers = new Set<() => void>();
let revision = 0;

const changed = () => {
  revision += 1;
  watchers.forEach((notify) => notify());
};

const shelfOf = (editor: DraftEditor): Map<string, Kept<unknown>> => {
  let shelf = shelves.get(editor);

  if (!shelf) {
    shelf = new Map();
    shelves.set(editor, shelf);
  }

  return shelf;
};

/** Makes what is kept this user's. Whatever anybody else left goes: whoever comes next starts with none. */
const handOver = (userId: string) => {
  if (owner === userId) return;

  const held = [...shelves.values()].some((shelf) => shelf.size > 0);

  shelves.forEach((shelf) => shelf.clear());
  owner = userId;

  if (held) changed();
};

/**
 * Whether a reload or a close of the tab would lose work at this moment: an editor on screen
 * says it holds something unsettled, or whoever is signed in has left a draft with something
 * to save in it, or has a write still on its way. Never on behalf of somebody who has signed
 * out, whose drafts are nobody's to be asked about until they are back.
 */
export const leavingLosesWork = (): boolean => {
  if (onScreen.size > 0) return true;
  if (signedIn === undefined) return false;

  const keeps = owner === signedIn
    && [...shelves.values()].some((shelf) => [...shelf.values()].some((kept) => kept.unsaved));

  return keeps || [...underway.values()].includes(signedIn);
};

// One listener for the life of the tab, added with the first thing there is to ask about. It
// is not taken off again: an editor that is left takes its own effects with it, and the
// drafts and the writes it leaves behind are exactly what still has to be asked about.
let listening = false;

const listen = () => {
  if (listening) return;

  listening = true;
  window.addEventListener('beforeunload', (event) => {
    if (leavingLosesWork()) event.preventDefault();
  });
};

/**
 * The kept drafts of one editor. Each editor names the shape of its own drafts, and nothing
 * else keeps anything under its name.
 */
export const keptDraftsOf = <T>(editor: DraftEditor): KeptDrafts<T> => {
  const shelf = shelfOf(editor) as Map<string, Kept<T>>;

  const isOwner = (userId: string | undefined): boolean => userId !== undefined && userId === owner;

  const put = (key: string, draft: T, unsaved: boolean) => {
    const kept = shelf.get(key);

    if (kept && kept.draft === draft && kept.unsaved === unsaved) {
      // Nothing a list would show differently, but it was kept again all the same.
      kept.keptAt = Date.now();

      return;
    }

    listen();
    shelf.set(key, { draft, unsaved, keptAt: Date.now() });
    changed();
  };

  const remove = (key: string) => {
    if (shelf.delete(key)) changed();
  };

  return {
    read: (userId, key) => (isOwner(userId) ? shelf.get(key)?.draft : undefined),
    readAll: (userId) => (isOwner(userId) ? Object.fromEntries([...shelf].map(([key, kept]) => [key, kept.draft])) : {}),
    keep: (userId, key, draft, unsaved) => {
      if (userId === undefined) return;

      handOver(userId);
      put(key, draft, unsaved ?? shelf.get(key)?.unsaved ?? true);
    },
    drop: (userId, key) => {
      if (userId === undefined) return;

      handOver(userId);
      remove(key);
    },
    leave: (userId, key, draft, unsaved = true) => {
      if (!isOwner(userId)) return;

      if (draft === undefined) remove(key);
      else put(key, draft, unsaved);
    },
  };
};

/** What this user has left unsaved in an editor: the keys of the drafts that hold something to save. */
export const unsavedDraftsOf = (editor: DraftEditor, userId: string | undefined): string[] => (
  userId !== undefined && userId === owner
    ? [...shelfOf(editor)].filter(([, kept]) => kept.unsaved).map(([key]) => key)
    : []
);

const watch = (notify: () => void) => {
  watchers.add(notify);

  return () => {
    watchers.delete(notify);
  };
};

const revisionNow = () => revision;

/**
 * `unsavedDraftsOf`, for a list that marks what its reader left unsaved in the editor a row
 * leads to. It follows the kept drafts for as long as the list is on screen.
 */
export const useUnsavedDrafts = (editor: DraftEditor, userId: string | undefined): ReadonlySet<string> => {
  useSyncExternalStore(watch, revisionNow);

  return new Set(unsavedDraftsOf(editor, userId));
};

/**
 * Gives up what this user left of things a list no longer holds: somebody else deleted them,
 * the team that owned them is gone, or the user was taken out of it. No row is left to carry
 * the mark of such a draft and no editor to discard it in, so a reload would go on asking
 * about it for as long as the tab lives.
 *
 * `listed` is what the list holds, and `since` the moment from which it says something of
 * a draft: only one last kept before that goes, because a list says nothing of what came
 * after it was read. Which moment that is depends on how the things of a list are opened,
 * and is the caller's to say. A report design is opened by a read of its own, so one made
 * a moment ago can have a draft while the list at hand, read before the design was made,
 * does not hold it, and the read that brings it waits for every other request. For such a
 * list the moment is when the server was asked for it: when it was answered is not enough,
 * as it can have been on its way while the design was made. A POI type is opened from the
 * catalogue's own row, so a draft of one was kept under a catalogue that held the type, and
 * a catalogue that came after it speaks from the moment it was answered.
 *
 * Like `leave`, this changes nothing unless the kept drafts are that user's: a list on
 * screen is no reason to take away what somebody else left.
 */
export const dropDraftsNotListed = (
  editor: DraftEditor, userId: string | undefined, listed: Iterable<string>, since: number,
): void => {
  if (userId === undefined || userId !== owner) return;

  const shelf = shelfOf(editor);
  const held = new Set(listed);
  const gone = [...shelf].filter(([key, kept]) => !held.has(key) && kept.keptAt < since).map(([key]) => key);

  gone.forEach((key) => shelf.delete(key));

  if (gone.length > 0) changed();
};

/**
 * Says who is signed in, or that nobody is. Called by the session's code beside every
 * `setAuth` and `removeAuth`, because neither signing in nor a session's end reloads the
 * tab. What somebody else left unsaved goes the moment another user signs in; a session
 * that only ended leaves it, for the same person coming back.
 */
export const followSession = (userId: string | undefined): void => {
  signedIn = userId;

  if (userId !== undefined) handOver(userId);
};

/** Has a reload or a close of the tab ask first, until what it answers is called. */
export const askBeforeLeaving = (): (() => void) => {
  const claim = Symbol('unsettled');

  listen();
  onScreen.add(claim);

  return () => {
    onScreen.delete(claim);
  };
};

/**
 * For an editor on screen: while it says it is `unsettled`, a reload or a close of the tab
 * asks first. Each editor has its own notion of that (a draft that differs from what is
 * saved, a file on its way), and this is asked in every such case whatever is kept.
 */
export const useAskBeforeLeaving = (unsettled: boolean): void => {
  useEffect(() => (unsettled ? askBeforeLeaving() : undefined), [unsettled]);
};

/**
 * Has a reload or a close of the tab ask first until a write has ended, on behalf of whoever
 * is signed in as it is queued. For a write that goes on after its editor was left: one
 * that waits behind another is never sent at all if the tab goes first.
 */
export const askBeforeLeavingUntil = (ended: Promise<unknown>): void => {
  if (signedIn === undefined) return;

  const done = () => {
    underway.delete(ended);
  };

  listen();
  underway.set(ended, signedIn);
  void ended.then(done, done);
};
